import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionCard, EmptyState, Loading } from "@/components/common/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { formatCount } from "@/lib/format";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Save, Plus, Lock, Users, Eye, Home, Target } from "lucide-react";

const FY_MONTHS = ["Apr-26", "May-26", "Jun-26", "Jul-26", "Aug-26", "Sep-26", "Oct-26", "Nov-26", "Dec-26", "Jan-27", "Feb-27", "Mar-27"];
const QUARTER_OF = (mo) => { const i = FY_MONTHS.indexOf(mo); return i < 0 ? "Q1" : `Q${Math.floor(i / 3) + 1}`; };
const ADD_VERTICALS = ["ATL/BTL", "Digital", "CX", "ATL", "BTL", "CX-Referral"];

// Leads -> Site Visits -> Bookings is the canonical funnel (per CEO). We also keep
// Qualified so no existing data is lost, but the emphasis is the 3 headline stages.
const STAGES = [
  { key: "leads", label: "Leads", icon: Users, accent: "hsl(var(--chart-cat-4))" },
  { key: "sv", label: "Site Visits", icon: Eye, accent: "hsl(var(--chart-cat-3))" },
  { key: "booking", label: "Bookings", icon: Home, accent: "hsl(var(--success))" },
  { key: "qualified_leads", label: "Qualified", icon: Target, accent: "hsl(var(--chart-cat-6))" },
];
const NUM = (v) => (v === "" || v == null ? 0 : (isNaN(parseFloat(v)) ? 0 : parseFloat(v)));

/**
 * Funnel entry — the SINGLE source of truth for funnel data (leads / site visits /
 * bookings, target + achieved) per project × month × vertical. Writes straight to
 * the backend `funnel_data` flow so the Dashboard and Targets always reconcile.
 */
export function FunnelEntryTab() {
  const { canEditConfig, isFull } = useAuth();
  const canEdit = canEditConfig || isFull;
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState("");
  const [month, setMonth] = useState("Apr-26");
  const [drafts, setDrafts] = useState({}); // id -> { field: value }
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newVert, setNewVert] = useState("Digital");

  const load = async () => {
    setLoading(true);
    try {
      const d = await api.dataListFunnel();
      const rows = d.rows || d || [];
      setAll(rows);
      setProject((p) => p || (rows[0] && rows[0].project) || "");
    } catch (e) { /* noop */ }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const projects = useMemo(() => Array.from(new Set(all.map((r) => r.project).filter(Boolean))).sort(), [all]);
  const visible = useMemo(
    () => all.filter((r) => r.project === project && r.month === month)
      .sort((a, b) => (a.vertical || "").localeCompare(b.vertical || "")),
    [all, project, month]
  );

  const valOf = (row, f) => (drafts[row.id] && drafts[row.id][f] !== undefined ? drafts[row.id][f] : (row[f] ?? 0));
  const setVal = (id, f, v) => setDrafts((d) => ({ ...d, [id]: { ...(d[id] || {}), [f]: v } }));
  const dirtyCount = Object.keys(drafts).length;

  const totals = useMemo(() => {
    const t = { leads_actual: 0, leads_target: 0, sv_actual: 0, sv_target: 0, booking_actual: 0, booking_target: 0 };
    visible.forEach((row) => {
      ["leads_actual", "leads_target", "sv_actual", "sv_target", "booking_actual", "booking_target"].forEach((f) => {
        t[f] += NUM(valOf(row, f));
      });
    });
    return t;
  }, [visible, drafts]);

  const save = async () => {
    setSaving(true);
    try {
      const ids = Object.keys(drafts);
      for (const id of ids) {
        const body = {};
        Object.entries(drafts[id]).forEach(([f, v]) => { body[f] = NUM(v); });
        await api.dataUpdateFunnel(id, body);
      }
      toast.success(`Saved ${ids.length} funnel row(s) \u2014 dashboard updated`);
      setDrafts({});
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed \u2014 Leader/Admin only");
    } finally { setSaving(false); }
  };

  const addRow = async () => {
    if (!project) { toast.error("Select a project first"); return; }
    if (visible.some((r) => r.vertical === newVert)) { toast.error(`${newVert} already exists for ${month}`); return; }
    setAdding(true);
    try {
      await api.dataCreateFunnel({
        project, month, quarter: QUARTER_OF(month), vertical: newVert,
        leads_target: 0, leads_actual: 0, qualified_leads_target: 0, qualified_leads_actual: 0,
        sv_target: 0, sv_actual: 0, booking_target: 0, booking_actual: 0,
      });
      toast.success(`Added ${newVert} row`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed \u2014 Leader/Admin only");
    } finally { setAdding(false); }
  };

  if (loading && all.length === 0) return <div className="p-4"><Loading rows={6} /></div>;

  return (
    <div className="space-y-4" data-testid="funnel-entry-tab">
      {/* headline totals */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {STAGES.slice(0, 3).map((s) => {
          const Icon = s.icon;
          const a = totals[`${s.key}_actual`] || 0;
          const t = totals[`${s.key}_target`] || 0;
          const att = t ? Math.round((a / t) * 100) : null;
          return (
            <div key={s.key} className="relative overflow-hidden rounded-xl border bg-card p-4 card-shadow" data-testid={`funnel-entry-total-${s.key}`}>
              <span className="absolute left-0 top-0 h-full w-1" style={{ background: s.accent }} />
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-muted-foreground">{s.label} · {month}</p>
                <span className="flex h-7 w-7 items-center justify-center rounded-lg" style={{ background: `${s.accent}1f`, color: s.accent }}><Icon className="h-4 w-4" /></span>
              </div>
              <p className="mt-2 font-heading text-xl font-semibold tabular-nums" style={{ color: s.accent }}>
                {formatCount(a)} <span className="text-sm font-normal text-muted-foreground">/ {formatCount(t)}</span>
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">{att != null ? `${att}% of target` : "No target set"}</p>
            </div>
          );
        })}
      </div>

      <SectionCard
        title="Funnel Entry"
        description="The single place to record Leads, Site Visits & Bookings (target + achieved) per project / month / vertical. Saved values flow straight to the Dashboard funnel and Targets — no separate grid to keep in sync."
        testId="funnel-entry-card"
        action={
          <div className="flex flex-wrap items-center gap-2">
            <Select value={project} onValueChange={setProject}>
              <SelectTrigger className="h-9 w-[180px]" data-testid="funnel-entry-project"><SelectValue placeholder="Project" /></SelectTrigger>
              <SelectContent className="max-h-72">{projects.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="h-9 w-[120px]" data-testid="funnel-entry-month"><SelectValue placeholder="Month" /></SelectTrigger>
              <SelectContent>{FY_MONTHS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
            {canEdit ? (
              <Button onClick={save} disabled={saving || dirtyCount === 0} data-testid="funnel-entry-save">
                <Save className="mr-1.5 h-4 w-4" />{saving ? "Saving\u2026" : `Save${dirtyCount ? ` (${dirtyCount})` : ""}`}
              </Button>
            ) : (
              <span className="inline-flex items-center gap-1.5 rounded-lg border bg-secondary px-3 py-2 text-xs text-muted-foreground"><Lock className="h-3.5 w-3.5" /> Read-only</span>
            )}
          </div>
        }
      >
        {!project ? (
          <div className="p-4"><EmptyState title="No funnel data yet" message="Add your first funnel row using the control below." /></div>
        ) : (
          <>
            <div className="table-scroll" style={{ maxHeight: "calc(100vh - 420px)" }}>
              <table className="w-full border-collapse" data-testid="funnel-entry-table">
                <thead>
                  <tr>
                    <th rowSpan={2} className="th-sticky th-sticky-left px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Vertical</th>
                    {STAGES.map((s) => (
                      <th key={s.key} colSpan={2} className="th-sticky border-l px-3 py-1.5 text-center text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{s.label}</th>
                    ))}
                  </tr>
                  <tr>
                    {STAGES.map((s) => (
                      <React.Fragment key={s.key}>
                        <th className="th-sticky border-l px-2 py-1.5 text-right text-[10px] font-medium text-muted-foreground">Target</th>
                        <th className="th-sticky px-2 py-1.5 text-right text-[10px] font-medium text-muted-foreground">Achieved</th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visible.length === 0 ? (
                    <tr><td colSpan={1 + STAGES.length * 2} className="px-3 py-10 text-center text-sm text-muted-foreground">No funnel rows for {project} · {month}. Add one below.</td></tr>
                  ) : visible.map((row, i) => (
                    <tr key={row.id} data-testid={`funnel-entry-row-${row.vertical}`} className={cn("border-b last:border-0", i % 2 && "row-zebra")}>
                      <td className="td-sticky-left px-3 py-1.5 text-sm font-medium">{row.vertical}</td>
                      {STAGES.map((s) => (
                        <React.Fragment key={s.key}>
                          <td className="border-l px-1 py-1.5">
                            <Input type="number" min="0" value={valOf(row, `${s.key}_target`)} disabled={!canEdit}
                              onChange={(e) => setVal(row.id, `${s.key}_target`, e.target.value)}
                              className="h-8 w-[76px] text-right text-sm tabular-nums disabled:opacity-60" data-testid={`funnel-entry-${row.vertical}-${s.key}-target`} />
                          </td>
                          <td className="px-1 py-1.5">
                            <Input type="number" min="0" value={valOf(row, `${s.key}_actual`)} disabled={!canEdit}
                              onChange={(e) => setVal(row.id, `${s.key}_actual`, e.target.value)}
                              className="h-8 w-[76px] text-right text-sm tabular-nums disabled:opacity-60" data-testid={`funnel-entry-${row.vertical}-${s.key}-actual`} />
                          </td>
                        </React.Fragment>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {canEdit && (
              <div className="flex flex-wrap items-end gap-2 border-t p-4" data-testid="funnel-entry-add-row">
                <div className="space-y-1">
                  <p className="text-[11px] font-medium text-muted-foreground">Add a vertical</p>
                  <Select value={newVert} onValueChange={setNewVert}>
                    <SelectTrigger className="h-9 w-44" data-testid="funnel-entry-new-vertical"><SelectValue /></SelectTrigger>
                    <SelectContent>{ADD_VERTICALS.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Button variant="outline" onClick={addRow} disabled={adding} data-testid="funnel-entry-add-button">
                  <Plus className="mr-1.5 h-4 w-4" /> {adding ? "Adding\u2026" : "Add row"}
                </Button>
              </div>
            )}
          </>
        )}
      </SectionCard>
    </div>
  );
}
